import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import jwtDecode from 'jwt-decode'

interface User {
  id: string
  email: string
  name: string
  role: 'user' | 'admin'
  subscription: {
    plan: 'free' | 'basic' | 'premium'
    modules: string[]
  }
  picture?: string
  isGoogleUser?: boolean
  lastLoginMethod?: 'email' | 'google'
  isEmailVerified?: boolean
  phone?: string
  company?: string
  address?: string
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (token: string) => void
  logout: () => void
  setUser: (user: User) => void
  setLoading: (loading: boolean) => void
  validateToken: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: (token: string) => {
        try {
          const decoded = jwtDecode(token) as any
          const user: User = {
            id: decoded.sub,
            email: decoded.email,
            name: decoded.name,
            role: decoded.role || 'user',
            subscription: decoded.subscription || {
              plan: 'free',
              modules: []
            },
            picture: decoded.picture,
            isGoogleUser: decoded.isGoogleUser,
            lastLoginMethod: decoded.lastLoginMethod,
            isEmailVerified: decoded.isEmailVerified,
            phone: decoded.phone,
            company: decoded.company,
            address: decoded.address
          }
          
          set({
            token,
            user,
            isAuthenticated: true,
            isLoading: false
          })
        } catch (error) {
          console.error('Error decoding token:', error)
          set({ 
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false 
          })
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      validateToken: () => {
        const state = get()
        if (state.token) {
          try {
            const decoded = jwtDecode(state.token) as any
            const currentTime = Date.now() / 1000
            
            if (decoded.exp && decoded.exp < currentTime) {
              // Token is expired
              set({
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false
              })
            }
          } catch (error) {
            // Invalid token
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false
            })
          }
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
) 